import { FC, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Input, Button, Checkbox } from "antd";
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { postLoginApi } from "../../service/login";
import { AlertMessage } from "../../components/common/AlertMessage";
import { useAlert } from "../../hooks/useAlert";
import { ALERT_Z_INDEX } from "../../utils/alertManager";
import { APP_NAME, COPYRIGHT_TEXT, SHANHAI_LOGO } from "../../config/constants";
import "./Login.scss";
import "../../styles/reset.scss";
import { UserRoleManager } from '../../utils/userRole';
import { InitService } from '../../utils/initService';

interface Props { }

export const Login: FC<Props> = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { alertConfig, showAlert, hideAlert, showSuccess, showError, showWarning, showInfo } = useAlert();

  // 组件加载时清除可能存在的旧token
  useEffect(() => {
    // 清除可能过期的token
    localStorage.removeItem("token");
    localStorage.removeItem("userInfo");

    // 检查是否有token过期标记
    const tokenExpired = localStorage.getItem('tokenExpired');
    if (tokenExpired === 'true') {
      // 清除标记
      localStorage.removeItem('tokenExpired');
      // 显示token过期提示
      showError('登录验证失效，请重新登录');
    }

    // 检查是否有注册成功标记
    const registerSuccess = localStorage.getItem("registerSuccess");
    const registerSuccessMessage = localStorage.getItem("registerSuccessMessage");

    if (registerSuccess === "true" && registerSuccessMessage) {
      showSuccess(registerSuccessMessage);
      // 清除标记
      localStorage.removeItem("registerSuccess");
      localStorage.removeItem("registerSuccessMessage");
    }
  }, [showError, showSuccess]);



  const onFinish = async (formData: Login.PostLoginPayloadType) => {

    if (!formData.username || !formData.username.trim()) {
      showError('请输入账号');
      return;
    }

    if (!formData.password || !formData.password.trim()) {
      showError('请输入密码');
      return;
    }

    setIsLoading(true);

    try {
      // 将表单数据与「记住我」状态组装成真正的登录请求参数
      const payload: Login.PostLoginPayloadType = {
        username: formData.username,
        password: formData.password,
        rememberMe, // 来自勾选框的当前状态
      };

      const response = await postLoginApi(payload);

      // 新的登录接口响应结构：{ code, data: { userId, accessToken, ... }, msg }
      if (response.code === 0 && response.data && response.data.accessToken) {
        const { accessToken, refreshToken, expiresTime, userId } = response.data;

        // 保存访问令牌（这里仍统一存到 localStorage，后端依据 rememberMe 决定有效期）
        localStorage.setItem("token", accessToken);

        // 可选：保存刷新令牌与过期时间，便于后续扩展
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("tokenExpiresTime", expiresTime);
        localStorage.setItem("userId", String(userId));

        // 创建用户信息并设置角色
        const userInfo = {
          username: formData.username,
          nickname: formData.username
        };

        // 更新用户角色信息
        UserRoleManager.updateUserRoleInfo(userInfo);

        // 保存用户信息
        localStorage.setItem("userInfo", JSON.stringify(userInfo));

        // 设置登录成功标记，用于首页显示提示
        localStorage.setItem("loginSuccess", "true");
        localStorage.setItem("loginSuccessMessage", response.msg || '登录成功！');

        // 清除之前的初始化标记，确保跳转后会重新初始化用户数据
        InitService.clearUserData();

        // 跳转到首页
        window.location.href = "/home";
      } else {
        // 登录失败，根据返回的 msg 显示错误信息
        const errorMessage = response.msg || '登录失败，请检查账号、密码或租户信息';
        showError(errorMessage);
      }
    } catch (error: any) {

      // 处理500错误，显示error字段中的信息
      if (error.status === 403) {
        showError('访问被拒绝，请检查用户名和密码是否正确');
      } else if (error.status === 415) {
        showError('请求格式错误，请检查Content-Type设置');
      } else if (error.status === 401) {
        showError('用户名或密码错误');
      } else if (error.status === 404) {
        showError('登录接口不存在，请联系管理员');
      } else if (error.status === 'NETWORK_ERROR') {
        showError('网络连接失败，请检查网络连接或服务器状态');
      } else if (error.message && error.message.includes('Failed to fetch')) {
        showError('无法连接到服务器，请检查网络连接');
      } else {
        // 优先显示error字段中的信息，如果没有则显示message
        const errorMessage = error.error || error.message || '登录失败，请稍后重试';
        showError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    showInfo('功能开发中...');
  };

  const handleRegister = () => {
    navigate("/register");
  };


  const handleTestConnection = async () => {
    try {
      // 使用service中封装的接口进行连接测试
      const testData: Login.PostLoginPayloadType = {
        username: 'test',
        password: 'test',
      };
      const response = await postLoginApi(testData);
      showInfo('服务器连接正常');
    } catch (error: any) {
      if (error.status === 401) {
        showInfo('服务器连接正常，但测试账号验证失败（这是预期的）');
      } else {
        showError('无法连接到服务器');
      }
    }
  };

  return (
    <div className="login-page">
      {/* Alert提示组件 - 页面顶部 */}
      <AlertMessage
        config={alertConfig}
        onClose={hideAlert}
        zIndex={ALERT_Z_INDEX.LOGIN}
      />

      {/* 左上角Logo和标题 */}
      <div className="header-logo">
        <div className="logo-container">
          <div className="logo-icon">
            <img src={SHANHAI_LOGO} alt={APP_NAME} className="logo-img" />
          </div>
          <h1 className="logo-title">{APP_NAME}</h1>
        </div>
      </div>

      <div className="login-container">
        {/* 登录表单 */}
        <div className="login-form">
          {/* 标题区域 */}
          <div className="form-header">
            <h2 className="form-title">登录</h2>
          </div>

          <Form onFinish={onFinish}>
            {/* 账号 */}
            <div className="form-group">
              <label htmlFor="username" className="form-label">
                账号
              </label>
              <Form.Item
                name="username"
                className="form-item-wrapper"
                rules={[
                  { required: true, message: '请输入账号' },
                  { min: 1, message: '账号不能为空' }
                ]}
              >
                <Input
                  placeholder="请输入账号"
                  className="form-input username-input"
                  disabled={isLoading}
                />
              </Form.Item>
            </div>

            {/* 密码 */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                密码
              </label>
              <Form.Item
                name="password"
                className="form-item-wrapper"
                rules={[
                  { required: true, message: '请输入密码' },
                  { min: 1, message: '密码不能为空' }
                ]}
              >
                <Input.Password
                  placeholder="请输入密码"
                  className="form-input password-input"
                  disabled={isLoading}
                  iconRender={(visible) => (
                    visible ? (
                      <EyeOutlined style={{ color: '#999999' }} />
                    ) : (
                      <EyeInvisibleOutlined style={{ color: '#999999' }} />
                    )
                  )}
                />
              </Form.Item>
            </div>

            {/* 记住我和忘记密码 */}
            <div className="form-options">
              <Form.Item name="rememberMe" valuePropName="checked" className="remember-me-item">
                <Checkbox
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="remember-checkbox"
                >
                  记住我
                </Checkbox>
              </Form.Item>
              <a href="#" className="forgot-password" onClick={handleForgotPassword}>
                忘记密码?
              </a>
            </div>

            {/* 登录按钮 */}
            <Form.Item className="form-item-wrapper">
              <Button
                htmlType="submit"
                className={`login-btn ${isLoading ? 'loading' : ''}`}
                disabled={isLoading}
                loading={isLoading}
              >
                {isLoading ? '登录中...' : '登录'}
              </Button>
            </Form.Item>
          </Form>

          {/* 注册链接 */}
          <div className="register-link">
            <span>没有账号? </span>
            <a href="#" className="register-now" onClick={handleRegister}>立即注册</a>
          </div>
        </div>
      </div>

      {/* 页脚版权信息 */}
      <div className="footer-copyright">
        {COPYRIGHT_TEXT}
      </div>
    </div>
  );
}; 